import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Buildings, UploadSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader } from "../components/shared";
import { SampleDataButton } from "../components/GettingStarted";
import { ClosedPeriods } from "../components/ClosedPeriods";
import { NumberSeries } from "../components/NumberSeries";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE_OPTIONS = [
  ["admin", "Administrator — full access"],
  ["accountant", "Accountant — finance access"],
  ["sales", "Sales — sales access"],
  ["purchasing", "Purchasing — purchasing access"],
  ["viewer", "Viewer — read-only"],
];

const FIELDS = [
  ["name", "Company Name"], ["legal_name", "Legal Name"],
  ["address", "Address"], ["address2", "Address 2"],
  ["city", "City"], ["state", "State / Province"],
  ["postcode", "Postcode"], ["country", "Country"],
  ["phone", "Phone"], ["email", "Email"],
  ["website", "Website"], ["tax_number", "Tax / ABN No."],
  ["registration_number", "Registration No."],
];

const BANK_FIELDS = [
  ["payment_terms", "Payment Terms (e.g. Net 30 days)"],
  ["bank_name", "Bank Name"],
  ["bank_account_name", "Account Name"],
  ["bank_bsb", "BSB"],
  ["bank_account_number", "Account Number"],
  ["bank_swift", "SWIFT / BIC"],
];

export default function CompanyProfile() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data: profile } = useQuery({ queryKey: ["company-profile"], queryFn: async () => (await api.get("/company-profile")).data });
  const [form, setForm] = useState(null);
  useEffect(() => { if (profile) setForm({ ...profile }); }, [profile]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => (await api.put("/company-profile", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["company-profile"] }); toast.success("Company profile saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Please choose an image under 2.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setF("logo", reader.result);
    reader.readAsDataURL(file);
  };

  if (user?.role !== "admin") {
    return (
      <div className="animate-fade-up border border-border bg-card p-10 text-center">
        <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-bold">Administrators only</h2>
        <p className="text-sm text-muted-foreground mt-1">You need the admin role to edit the company profile.</p>
      </div>
    );
  }
  if (!form) return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-up" data-testid="company-profile-page">
      <SectionHeader title="Company Profile" subtitle="Branding on your documents">
        <div className="flex items-center gap-2">
          <SampleDataButton />
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-company-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">Save Profile</Button>
        </div>
      </SectionHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 border border-border bg-card p-5">
          <p className="font-bold text-sm mb-4">Company Details</p>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {FIELDS.map(([k, lbl]) => (
              <div key={k}>
                <Label className="overline-label">{lbl}</Label>
                <Input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="mt-1 rounded-sm" />
              </div>
            ))}
          </div>
          <p className="font-bold text-sm mb-4 mt-6 pt-5 border-t border-border">Payment Details <span className="font-normal text-muted-foreground text-xs">— shown on invoice PDFs</span></p>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {BANK_FIELDS.map(([k, lbl]) => (
              <div key={k}>
                <Label className="overline-label">{lbl}</Label>
                <Input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="mt-1 rounded-sm" />
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1 border border-border bg-card p-5">
          <p className="font-bold text-sm mb-4 flex items-center gap-2"><Buildings size={16} className="text-primary" /> Company Logo</p>
          <div className="border border-dashed border-border rounded-sm p-4 flex flex-col items-center justify-center min-h-[160px] bg-secondary/30">
            {form.logo ? (
              <img src={form.logo} alt="Company logo" data-testid="company-logo-preview" className="max-h-32 max-w-full object-contain" />
            ) : (
              <p className="text-sm text-muted-foreground text-center">No logo uploaded.<br />Used on invoices &amp; printouts.</p>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="company-logo-input" />
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-logo-button" className="rounded-sm h-9 flex-1"><UploadSimple size={16} className="mr-1.5" /> Upload</Button>
            {form.logo && <Button variant="outline" onClick={() => setF("logo", "")} data-testid="remove-logo-button" className="rounded-sm h-9 text-destructive hover:text-destructive"><Trash size={16} /></Button>}
          </div>
          <p className="text-xs text-muted-foreground mt-3">PNG or JPG, transparent background recommended. Appears in the header of your invoice PDFs and document printouts.</p>
        </div>

        <div className="lg:col-span-3 border border-border bg-card p-5" data-testid="access-control-card">
          <p className="font-bold text-sm mb-1 flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Access Control</p>
          <p className="text-xs text-muted-foreground mb-4">Role automatically assigned to anyone who registers or signs in with Google for the first time. Set this to a restricted role once you've finished testing. Existing users are not affected.</p>
          <div className="max-w-sm">
            <Label className="overline-label">Default role for new sign-ups</Label>
            <Select value={form.default_user_role || "admin"} onValueChange={(v) => setF("default_user_role", v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="default-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(([v, lbl]) => <SelectItem key={v} value={v} data-testid={`default-role-option-${v}`}>{lbl}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="max-w-sm mt-5">
            <Label className="overline-label">Low margin alert below (%)</Label>
            <Input type="number" min={0} max={100} step="0.5" value={form.low_margin_pct ?? 20} onChange={(e) => setF("low_margin_pct", Number(e.target.value || 0))} className="mt-1 rounded-sm" data-testid="low-margin-pct-input" />
            <p className="text-[11px] text-muted-foreground mt-1">Sales quotes, orders and invoices below this margin are shown in red.</p>
          </div>
        </div>
        <NumberSeries />
        <ClosedPeriods />
      </div>
    </div>
  );
}
